"use client"

import { createContext, useContext, type ReactNode } from "react"

/**
 * Reports a failure a user-triggered action ended in. The app decides what is worth reporting
 * (apps/client wires this to `reportClientError`, which applies the fault policy); this package
 * cannot import Sentry, so it only calls whatever the app supplied. FIN-1497.
 */
export type ClientErrorReporter = (error: unknown, context: string) => void

const ClientErrorReporterContext = createContext<ClientErrorReporter | null>(null)

export function ClientErrorReporterProvider({
  report,
  children,
}: {
  report: ClientErrorReporter
  children: ReactNode
}) {
  return <ClientErrorReporterContext.Provider value={report}>{children}</ClientErrorReporterContext.Provider>
}

/** The app's reporter, or null when none is provided (tests, storybook). */
export function useClientErrorReporter(): ClientErrorReporter | null {
  return useContext(ClientErrorReporterContext)
}

const doNotReport: ClientErrorReporter = () => {}

/**
 * The app's reporter for a component's own `catch` blocks, a no-op when none is provided. Name
 * the result `reportClientError` so `local/no-silent-catch` recognises the call.
 */
export function useReportClientError(): ClientErrorReporter {
  return useContext(ClientErrorReporterContext) ?? doNotReport
}
